import { clearMirror } from './sessionMirror';

const USER_KEY_PREFIX = 'user:';

const USER_DATA_KEYS = [
  'performances_v1', 'performanceJournalTemplates_v1',
  'sessionHistory_v2', 'practiceItems_v2', 'practicePlans_v2',
  'currentPlanId_v2', 'reflectionQuestions_v2',
  'session_active', 'session_start_time', 'session_paused',
  'session_pause_time', 'session_active_items', 'session_total_time',
  'session_plan_items', 'session_selected_item',
  'session_current_plan_id', 'session_plan_label',
  'session_finalized', 'session_finalized_at',
  'audioRecordingSettings', 'practiceBlockTitles_cache',
  'pending_firestore_sessions', 'practice_tags',
  'completedPracticeDays', 'practiceAlarmSettings',
  'returning_from_practice',
  'lastStreakMilestoneCelebration', 'lastStreakContinuationCelebration',
];

export class StorageManager {
    constructor() {
      this.maxStorageSize = 5 * 1024 * 1024; // 5MB limit
      this.compressionThreshold = 1024 * 1024; // 1MB threshold for compression
      this._uid = null;
    }

    setUser(uid) {
      this._uid = uid;
    }

    clearUser() {
      this._uid = null;
    }

    get uid() {
      return this._uid;
    }

    userKey(key) {
      if (!this._uid) throw new Error('StorageManager: no active user. Call setUser(uid) first.');
      return `${USER_KEY_PREFIX}${this._uid}:${key}`;
    }

    setUserItem(key, value) {
      if (!this._uid) return false;
      try {
        localStorage.setItem(this.userKey(key), value);
        return true;
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          console.warn(`localStorage quota exceeded for user key: ${key}. Freeing space…`);
          this._freeUserSpace();
          try {
            localStorage.setItem(this.userKey(key), value);
            return true;
          } catch (retryError) {
            console.warn(`Could not save user key ${key} after clearing space`);
          }
        } else {
          console.error(`localStorage error for user key ${key}:`, error);
        }
        return false;
      }
    }

    getUserItem(key) {
      if (!this._uid) return null;
      try {
        const fullKey = this.userKey(key);
        const item = localStorage.getItem(fullKey);
        if (item !== null) return item;

        if (window.tempStorage && window.tempStorage[fullKey]) {
          return window.tempStorage[fullKey];
        }
        return null;
      } catch (error) {
        console.error(`Error retrieving user key ${key}:`, error);
        return null;
      }
    }

    removeUserItem(key) {
      if (!this._uid) return;
      try {
        localStorage.removeItem(this.userKey(key));
      } catch (error) {
        console.error(`Error removing user key ${key}:`, error);
      }
    }

    clearAllUserData(uid) {
      const prefix = `${USER_KEY_PREFIX}${uid}:`;
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k));

      // Also clear legacy uid-in-suffix keys (practiceDays, FreeFlow, etc.)
      const suffixKeysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (
          k.includes(`:${uid}`) ||
          k.includes(`_${uid}_`) ||
          k.includes(`_${uid}`)
        ) && !k.startsWith(prefix)) {
          suffixKeysToRemove.push(k);
        }
      }
      suffixKeysToRemove.forEach(k => localStorage.removeItem(k));

      try { indexedDB.deleteDatabase('flowframe-scores'); } catch (e) { console.warn('Failed to clear score cache:', e); }

      // Clear this user's rows from the IndexedDB session mirror so accounts
      // sharing a device never see each other's practice history.
      clearMirror(uid).catch((e) => console.warn('Failed to clear session mirror:', e));

      if (window.tempStorage) {
        Object.keys(window.tempStorage).forEach(k => {
          if (k.startsWith(prefix)) delete window.tempStorage[k];
        });
      }

      console.log(`Cleared all user data for uid: ${uid} (${keysToRemove.length + suffixKeysToRemove.length} keys)`);
    }

    migrateUnprefixedKeys(uid) {
      const migrationFlag = `${USER_KEY_PREFIX}${uid}:_migrated`;
      if (localStorage.getItem(migrationFlag)) return;

      let migratedCount = 0;
      for (const key of USER_DATA_KEYS) {
        const oldValue = localStorage.getItem(key);
        const newKey = `${USER_KEY_PREFIX}${uid}:${key}`;
        if (oldValue !== null && localStorage.getItem(newKey) === null) {
          try {
            localStorage.setItem(newKey, oldValue);
            localStorage.removeItem(key);
            migratedCount++;
          } catch (e) {
            console.warn(`Migration failed for key ${key}:`, e);
          }
        }
      }

      localStorage.setItem(migrationFlag, 'true');
      if (migratedCount > 0) {
        console.log(`Migrated ${migratedCount} localStorage keys to user namespace for uid: ${uid}`);
      }
    }

    _freeUserSpace() {
      if (!this._uid) return;
      const prefix = `${USER_KEY_PREFIX}${this._uid}:`;
      const SESSION_KEYS = [
        'session_active', 'session_start_time', 'session_paused',
        'session_pause_time', 'session_total_time', 'session_active_items',
        'session_plan_items', 'session_plan_label', 'session_current_plan_id',
        'session_selected_item', 'session_finalized', 'session_finalized_at',
      ];
      const protectedSuffixes = new Set(SESSION_KEYS);

      const expendable = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix)) {
          const suffix = k.slice(prefix.length);
          if (!protectedSuffixes.has(suffix)) {
            expendable.push(k);
          }
        }
      }
      expendable.sort((a, b) => {
        const aLen = (localStorage.getItem(a) || '').length;
        const bLen = (localStorage.getItem(b) || '').length;
        return bLen - aLen;
      });
      for (const k of expendable) {
        try { localStorage.removeItem(k); } catch {}
      }
    }

    // Check available storage space
    async getStorageEstimate() {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        return await navigator.storage.estimate();
      }
      return null;
    }
  
    // Calculate size of data in bytes
    calculateSize(data) {
      return new Blob([JSON.stringify(data)]).size;
    }
  
    // Clean old data to make space
    cleanOldData(key, maxItems = 20) {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return true;
  
        const data = JSON.parse(stored);
        if (Array.isArray(data) && data.length > maxItems) {
          // Keep only the most recent items (assuming they're sorted by date)
          const cleaned = data.slice(0, maxItems);
          localStorage.setItem(key, JSON.stringify(cleaned));
          console.log(`Cleaned ${key}: kept ${maxItems} most recent items`);
          return true;
        }
      } catch (error) {
        console.error(`Error cleaning ${key}:`, error);
        // If parsing fails, remove the corrupted data
        localStorage.removeItem(key);
      }
      return true;
    }
  
    // Compress data by removing unnecessary fields
    compressSessionData(sessions) {
      return sessions.map(session => ({
        id: session.id,
        date: session.date,
        totalTimeMs: session.totalTimeMs,
        // Keep essential session data that users expect to persist
        planLabel: session.planLabel?.substring(0, 200), // Increased limit
        reflection: session.reflection || {}, // 🔧 KEEP reflection data
        reflectionQuestions: session.reflectionQuestions || {}, // 🔧 KEEP reflection questions
        // Only keep essential item data but preserve more fields
        items: session.items?.map(item => ({
          id: item.id,
          title: item.title || item.name, // 🔧 Keep title
          name: item.name,
          timeSpent: item.timeSpent,
          actualElapsedMs: item.actualElapsedMs, // 🔧 Keep timing data
          rolledUpMs: item.rolledUpMs, // 🔧 Keep rolled up time
          completed: item.completed,
          tags: item.tags?.slice(0, 8), // 🔧 Increased tag limit
          notes: item.notes?.substring(0, 500), // 🔧 Keep notes but limit length
          // Keep tempo tracking data
          goalTempoEnabled: item.goalTempoEnabled,
          goalTempo: item.goalTempo,
          sessionGoalTempo: item.sessionGoalTempo,
          startTempo: item.startTempo,
          endTempo: item.endTempo,
          // Keep child structure but compress children recursively
          children: item.children?.map(child => ({
            id: child.id,
            title: child.title || child.name,
            actualElapsedMs: child.actualElapsedMs,
            rolledUpMs: child.rolledUpMs,
            tags: child.tags?.slice(0, 5),
            notes: child.notes?.substring(0, 200), // Shorter for children
          })) || []
        })) || [],
        // Remove only truly large/unnecessary fields like raw audio data
        // but keep everything users actively create
      }));
    }
  
    _resolveKey(key) {
      if (this._uid && USER_DATA_KEYS.includes(key)) {
        return this.userKey(key);
      }
      return key;
    }

    // Safe storage with quota management
    async safeSetItem(key, data, options = {}) {
      const { 
        maxItems = 20, 
        compress = true, 
        fallbackToMemory = true 
      } = options;

      const resolvedKey = this._resolveKey(key);

      try {
        const estimate = await this.getStorageEstimate();
        
        let processedData = data;
        
        if (compress && key === 'sessionHistory_v2' && Array.isArray(data)) {
          processedData = this.compressSessionData(data);
          
          if (processedData.length > maxItems) {
            processedData = processedData.slice(0, maxItems);
            console.log(`Limited ${key} to ${maxItems} items`);
          }
        }
  
        const dataSize = this.calculateSize(processedData);
        
        if (estimate) {
          const availableSpace = estimate.quota - estimate.usage;
          if (dataSize > availableSpace * 0.8) {
            console.warn(`Data size (${dataSize}) approaching quota limit`);
            this.cleanOldData(resolvedKey, Math.floor(maxItems / 2));
            if (Array.isArray(processedData)) {
              processedData = processedData.slice(0, Math.floor(maxItems / 2));
            }
          }
        }
  
        localStorage.setItem(resolvedKey, JSON.stringify(processedData));
        return { success: true, itemsStored: Array.isArray(processedData) ? processedData.length : 1 };
  
      } catch (error) {
        if (error.name === 'QuotaExceededError') {
          console.warn(`Storage quota exceeded for ${resolvedKey}, attempting cleanup...`);
          
          const cleanupStrategies = [
            () => this.cleanOldData(resolvedKey, 15),
            () => this.cleanOldData(resolvedKey, 10),
            () => this.cleanOldData(resolvedKey, 5),
            () => localStorage.removeItem(resolvedKey),
          ];
  
          for (const cleanup of cleanupStrategies) {
            try {
              cleanup();
              
              const reducedData = Array.isArray(processedData) 
                ? processedData.slice(0, 5) 
                : processedData;
              
              localStorage.setItem(resolvedKey, JSON.stringify(reducedData));
              console.log(`Successfully stored ${resolvedKey} after cleanup`);
              return { 
                success: true, 
                itemsStored: Array.isArray(reducedData) ? reducedData.length : 1,
                warning: 'Storage cleaned due to quota limits'
              };
            } catch (retryError) {
              if (retryError.name !== 'QuotaExceededError') {
                throw retryError;
              }
            }
          }
  
          if (fallbackToMemory) {
            console.error(`All storage cleanup failed for ${resolvedKey}, falling back to memory`);
            window.tempStorage = window.tempStorage || {};
            window.tempStorage[resolvedKey] = processedData;
            return { 
              success: false, 
              error: 'Stored in memory only',
              itemsStored: Array.isArray(processedData) ? processedData.length : 1
            };
          }
  
          return { success: false, error: 'Storage quota exceeded' };
        }
        
        console.error(`Error storing ${resolvedKey}:`, error);
        return { success: false, error: error.message };
      }
    }
  
    // Safe getter with fallback to memory (auto-resolves user namespace)
    safeGetItem(key) {
      const resolvedKey = this._resolveKey(key);
      try {
        const item = localStorage.getItem(resolvedKey);
        if (item) {
          return JSON.parse(item);
        }
        
        if (window.tempStorage && window.tempStorage[resolvedKey]) {
          console.log(`Retrieved ${resolvedKey} from memory fallback`);
          return window.tempStorage[resolvedKey];
        }
        
        return null;
      } catch (error) {
        console.error(`Error retrieving ${resolvedKey}:`, error);
        localStorage.removeItem(resolvedKey);
        return null;
      }
    }
  
    async getStorageInfo() {
      const estimate = await this.getStorageEstimate();
      const sessionHistorySize = this.calculateSize(
        this.safeGetItem('sessionHistory_v2') || []
      );
      
      return {
        estimate,
        sessionHistorySize,
        sessionHistorySizeFormatted: this.formatBytes(sessionHistorySize),
        quota: estimate?.quota ? this.formatBytes(estimate.quota) : 'Unknown',
        usage: estimate?.usage ? this.formatBytes(estimate.usage) : 'Unknown',
      };
    }
  
    // Format bytes for display
    formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
  }
  
  export { USER_DATA_KEYS, USER_KEY_PREFIX };

  // Create singleton instance
  export const storageManager = new StorageManager();